import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';

/**
 * OAuth Callback Handler
 * Processes OAuth2 authorization returns from wearable device providers
 */
const OAuthCallback = ({ onNavigate }) => {
  const { user, userInfo, getAccessToken } = useAuth();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing device connection...');
  const [debugInfo, setDebugInfo] = useState(null);
  
  // Use ref to prevent duplicate processing
  const isProcessingRef = useRef(false);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate processing
    if (hasProcessedRef.current) {
      console.log('OAuth callback already processed, skipping...');
      return;
    }

    const handleOAuthCallback = async () => {
      // Double-check with ref to prevent race conditions
      if (isProcessingRef.current) {
        console.log('Already processing OAuth callback, skipping duplicate call');
        return;
      }
      
      isProcessingRef.current = true;
      hasProcessedRef.current = true;

      try {
        // Extract query parameters from current URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');
        const vendor = getVendorFromPath();

        console.log('🔐 OAuth Callback Started:', { vendor, hasCode: !!code, hasState: !!state, error });

        // Handle OAuth errors
        if (error) {
          setStatus('error');
          setMessage(`Device connection failed: ${error}`);
          setTimeout(() => onNavigate('dashboard'), 3000);
          return;
        }

        // Validate required parameters
        if (!code || !state) {
          setStatus('error');
          setMessage('Invalid callback parameters');
          setDebugInfo({ code: !!code, state: !!state, vendor });
          setTimeout(() => onNavigate('dashboard'), 3000);
          return;
        }

        // Check if this code has already been processed
        const processedCodes = JSON.parse(sessionStorage.getItem('processedOAuthCodes') || '[]');
        if (processedCodes.includes(code)) {
          console.log('This OAuth code has already been processed');
          setStatus('success');
          setMessage('Device already connected. Redirecting to dashboard...');
          setTimeout(() => onNavigate('dashboard'), 2000);
          return;
        }

        // Get OAuth state from localStorage or create a new one
        let oauthState = JSON.parse(localStorage.getItem('oauthState') || '{}');
        
        // If no valid OAuth state, create one for this session using REAL user ID only
        if (!oauthState.userId || !oauthState.vendor) {
          const userId = user?.localAccountId || user?.homeAccountId;
          
          // PRODUCTION: Never allow OAuth without real authenticated user
          if (!userId) {
            setStatus('error');
            setMessage('Authentication required. Please log in before connecting devices.');
            setTimeout(() => onNavigate('profile'), 3000);
            return;
          }
          
          oauthState = {
            userId: userId,
            vendor: vendor,
            timestamp: Date.now()
          };
          localStorage.setItem('oauthState', JSON.stringify(oauthState));
        }

        setMessage(`Connecting ${vendor}...`);

        // Exchange authorization code for tokens - ONLY use real authenticated user ID
        const userId = user?.localAccountId || user?.homeAccountId;
        
        // PRODUCTION: Ensure we have real user ID before proceeding
        if (!userId) {
          setStatus('error');
          setMessage('Authentication error: No user ID found. Please log in again.');
          setTimeout(() => onNavigate('profile'), 3000);
          return;
        }
        
        const userCountry = userInfo?.country || user?.idTokenClaims?.country || 'United States';
        
        console.log('🔑 User Info:', { userId, userCountry, vendor });

        const tokenExchange = await exchangeCodeForTokens({
          vendor: vendor,
          code: code,
          state: state,
          userId: userId,
          userCountry: userCountry
        });

        console.log('🔍 Token Exchange Result:', tokenExchange);

        if (tokenExchange.success) {
          // Mark this code as processed in sessionStorage only (temporary tracking)
          processedCodes.push(code);
          sessionStorage.setItem('processedOAuthCodes', JSON.stringify(processedCodes));

          // DON'T store in localStorage - connection status comes from data lake
          // The device connection is now stored in Azure Data Lake via the backend
          
          // Clear OAuth state (can remove once fully migrated to data lake)
          localStorage.removeItem('oauthState');

          setStatus('success');
          setMessage(`${vendor} connected successfully! Connection stored in secure data lake. Redirecting to profile...`);
          
          // Redirect to profile manager after success
          setTimeout(() => {
            onNavigate('profile');
          }, 1000);

        } else {
          setStatus('error');
          // Handle authentication-specific errors
          if (tokenExchange.authRequired) {
            setMessage('Authentication expired. Please log in again and retry device connection.');
          } else {
            setMessage(tokenExchange.error || 'Failed to connect device');
          }
          setDebugInfo(tokenExchange.debugInfo);
          setTimeout(() => {
            onNavigate('profile'); // Stay on profile page after error
          }, 5000);
        }

      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred during device connection');
        setDebugInfo({ error: error.message, stack: error.stack });
        setTimeout(() => {
          onNavigate('profile'); // Stay on profile page after error
        }, 5000);
      } finally {
        isProcessingRef.current = false;
      }
    };

    // Only run if we have user data
    if (user) {
      handleOAuthCallback();
    } else {
      console.log('Waiting for user data before processing OAuth callback...');
    }
  }, [user]); // Only depend on user to prevent multiple runs

  // Extract vendor from callback path
  const getVendorFromPath = () => {
    const pathParts = window.location.pathname.split('/');
    const vendor = pathParts[pathParts.length - 1];
    
    // Map callback paths to vendor names
    const vendorMap = {
      'oura': 'oura-ring',
      'fitbit': 'fitbit',
      'withings': 'withings',
      'garmin': 'garmin'
    };
    
    return vendorMap[vendor] || vendor;
  };

  // Exchange authorization code for access tokens
  const exchangeCodeForTokens = async (params) => {
    try {
      console.log('🔍 Sending request with params:', params);
      
      // Get authentication token
      const authToken = await getAccessToken();
      if (!authToken) {
        console.error('❌ No auth token available');
        return {
          success: false,
          error: 'Authentication token not available. Please log in again.',
          authRequired: true
        };
      }
      
      const apiUrl = process.env.REACT_APP_API_URL || 'https://medicureon-app-api.azurewebsites.net/api';
      const endpoint = `${apiUrl}/oauth/exchange`;
      
      console.log('🔍 API Endpoint:', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'X-User-Country': params.userCountry,
          'Accept': 'application/json'
        },
        body: JSON.stringify(params)
      });

      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response headers:', Object.fromEntries(response.headers));
      console.log('🔍 Response ok:', response.ok);

      // Get the response text first
      const responseText = await response.text();
      console.log('🔍 Raw response text:', responseText);
      console.log('🔍 Response text length:', responseText.length);

      // Check if we got an empty response
      if (!responseText || responseText.length === 0) {
        console.error('🔍 Empty response from server');
        return {
          success: false,
          error: 'Empty response from server',
          debugInfo: {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers)
          }
        };
      }

      // Try to parse as JSON
      let result;
      try {
        result = JSON.parse(responseText);
        console.log('🔍 Parsed JSON successfully:', result);
      } catch (jsonError) {
        console.error('🔍 JSON parse error:', jsonError);
        console.error('🔍 Response that failed to parse:', responseText);
        
        // Check if it's an HTML error page
        if (responseText.includes('<!DOCTYPE') || responseText.includes('<html')) {
          return {
            success: false,
            error: 'Server returned HTML instead of JSON',
            debugInfo: {
              status: response.status,
              isHtml: true,
              preview: responseText.substring(0, 200)
            }
          };
        }
        
        return {
          success: false,
          error: 'Invalid JSON response from server',
          debugInfo: {
            parseError: jsonError.message,
            responseText: responseText.substring(0, 500)
          }
        };
      }

      // Check if response indicates an error
      if (!response.ok || !result.success) {
        console.error('🔍 Server returned error:', result);
        return {
          success: false,
          error: result.error || `HTTP ${response.status}: ${response.statusText}`,
          debugInfo: {
            status: response.status,
            serverResponse: result
          }
        };
      }
      
      return result;

    } catch (error) {
      console.error('Token exchange error:', error);
      return {
        success: false,
        error: error.message,
        debugInfo: {
          errorType: error.name,
          stack: error.stack
        }
      };
    }
  };

  // Get status icon based on current status
  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return '⏳';
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '⏳';
    }
  };

  // Get status color based on current status
  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'text-blue-600';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-gray-50">
      <div className="w-full max-w-md">
        <div className="p-8 text-center bg-white rounded-lg shadow-lg">
          {/* Status Icon */}
          <div className="mb-4 text-6xl">
            {getStatusIcon()}
          </div>

          {/* Status Title */}
          <h1 className={`text-2xl font-bold mb-4 ${getStatusColor()}`}>
            {status === 'processing' && 'Connecting Device'}
            {status === 'success' && 'Connection Successful'}
            {status === 'error' && 'Connection Failed'}
          </h1>

          {/* Status Message */}
          <p className="mb-6 text-gray-600">
            {message}
          </p>

          {/* Loading Animation */}
          {status === 'processing' && (
            <div className="flex justify-center mb-6">
              <div className="w-8 h-8 border-b-2 rounded-full animate-spin border-primary-600"></div>
            </div>
          )}

          {/* Debug Information (only in development) */}
          {debugInfo && process.env.NODE_ENV === 'development' && (
            <div className="p-4 mt-4 text-xs text-left bg-gray-100 rounded">
              <h3 className="mb-2 font-bold">Debug Info:</h3>
              <pre className="overflow-auto whitespace-pre-wrap max-h-40">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {status === 'error' && (
              <>
                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-4 py-2 text-white transition-colors bg-gray-600 rounded-lg hover:bg-gray-700"
                >
                  Try Again
                </button>
                <button
                  onClick={() => onNavigate('dashboard')}
                  className="w-full px-4 py-2 text-white transition-colors rounded-lg bg-primary-600 hover:bg-primary-700"
                >
                  Return to Dashboard
                </button>
              </>
            )}
            
            {status === 'success' && (
              <div className="text-sm text-gray-500">
                Redirecting automatically in 2 seconds...
              </div>
            )}
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Having trouble? Contact support at{' '}
            <a href="mailto:support@medicureon.app" className="text-primary-600 hover:underline">
              support@medicureon.app
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;